// Router

// React
import { useEffect, useState } from 'react'

// Layout
import ScrollableScreen from '@/components/ui/ScrollableScreen'

// Mantine
import { Anchor, Menu, ActionIcon, Button, Modal, Select } from '@mantine/core'

// Icons
import {
    IconExternalLink,
    IconCloudDownload,
    IconDotsVertical,
} from '@tabler/icons-react'

// Next link
import { Link, useParams } from 'react-router-dom'
import RouterLink from '@/components/ui/RouterLink'

// Status
import {
    getEstimateStatus,
    getInvoiceStatus,
    estimate_statuses,
} from '@/lib/retailerStatuses'

// Lodash OrderBy
import orderBy from 'lodash/orderBy'

// Components
import RetailerEstimateFulfillmentOrders from '@/components/retailers/RetailerEstimateFulfillmentOrders'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// UI
import Badge from '@/components/ui/Badge'
import Box from '@/components/ui/Box'
import ComponentsAvailabilityChecker from '@/components/ui/ComponentsAvailabilityChecker'
import { RETAILER_ESTIMATES_LOCATION_ID } from '@/lib/constants'
// Dayjs
import dayjs from 'dayjs'

export default function DisplayRetailerEstimate() {
    const { id } = useParams()

    // State
    const [retailerEstimate, setRetailerEstimate] = useState(null)

    // Estimate status
    const estimateStatus = getEstimateStatus(retailerEstimate?.status)

    // Load the retailer estimate
    const { requestGet } = useAxiosApi()

    const loadRetailerEstimate = async () => {
        const { data } = await requestGet(`/retailer-estimates/${id}`)

        setRetailerEstimate(data)
    }

    useEffect(() => {
        if (id) loadRetailerEstimate()
    }, [id])

    // Breadcrumbs
    const breadcrumbs = [
        {
            label: 'Devis',
            href: '/retailer_estimates?status=accepted,draft,sent,expired,read,invoiced,advanced,partialinvoiced',
        },
        {
            label: retailerEstimate?.number,
        },
    ]

    // Edit status
    const [editStatusModalOpened, setEditStatusModalOpened] = useState(false)
    const [newStatus, setNewStatus] = useState()
    const [isSavingNewStatus, setIsSavingNewStatus] = useState(false)

    const { requestPost } = useAxiosApi()

    const statusOptions = orderBy(
        Object.keys(estimate_statuses).map((key) => ({
            label: estimate_statuses[key].label,
            value: key,
        })),
        'label'
    )

    const openModal = () => {
        setEditStatusModalOpened(true)
        setNewStatus(retailerEstimate.status)
        setIsSavingNewStatus(false)
    }

    const saveNewStatus = async () => {
        setIsSavingNewStatus(true)

        await requestPost(
            `/retailers/retailer-estimates/${retailerEstimate.id}/edit-status`,
            {
                status: newStatus,
            }
        )

        await loadRetailerEstimate()

        setEditStatusModalOpened(false)
        setIsSavingNewStatus(false)
    }

    const rightSection = (
        <div className="flex gap-2 items-center">
            <Badge color={estimateStatus.badgeColor}>
                {estimateStatus.label}
            </Badge>

            <Menu shadow="md" width={200} position="bottom-end">
                <Menu.Target>
                    <ActionIcon variant="default" size={36}>
                        <IconDotsVertical size={15} />
                    </ActionIcon>
                </Menu.Target>

                <Menu.Dropdown>
                    <Menu.Item onClick={openModal}>
                        Modifier le statut
                    </Menu.Item>
                    <Menu.Item
                        component={RouterLink}
                        href={`/retailer_estimates/create?retailer_id=${retailerEstimate?.retailer_id}&copy_estimate_id=${retailerEstimate?.id}`}
                    >
                        Dupliquer
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>
        </div>
    )

    // Reserved quantities
    const hasReservedQuantities =
        [
            'accepted',
            'advanced',
            'draft',
            'sent',
            'invoiced',
            'read',
            'partialinvoiced',
        ].includes(retailerEstimate?.status) &&
        retailerEstimate?.fulfillment_orders.filter(
            (fulfillment_order) =>
                !['CANCELLED', 'OPEN'].includes(fulfillment_order.status)
        ).length === 0

    // Estimate Box Right Section
    const EstimateBoxRightSection = (
        <div className="flex gap-2 items-center">
            <Badge color={estimateStatus.badgeColor}>
                {estimateStatus.label}
            </Badge>

            <Button
                variant="default"
                component="a"
                href={`https://www.sellsy.com/?_f=estimateOverview&id=${retailerEstimate?.sellsy_id}`}
                target="_blank"
                leftSection={<IconExternalLink size="1rem" />}
            >
                Ouvrir dans Sellsy
            </Button>
        </div>
    )

    const PDFBoxRightSection = (
        <div className="flex gap-2 items-center">
            <Button
                variant="default"
                component="a"
                href={retailerEstimate?.pdf_link}
                target="_blank"
                leftSection={<IconExternalLink size="1rem" />}
            >
                Ouvrir le PDF
            </Button>

            <div className="hidden">
                <Button
                    variant="default"
                    component="a"
                    href={retailerEstimate?.pdf_link}
                    download
                    leftSection={<IconCloudDownload size="1rem" />}
                >
                    Télécharger le PDF
                </Button>
            </div>
        </div>
    )

    return (
        <ScrollableScreen breadcrumbs={breadcrumbs} rightSection={rightSection}>
            {retailerEstimate && (
                <>
                    <div className="p-5">
                        <div className="mx-auto max-w-[900px] flex flex-col gap-5">
                            {hasReservedQuantities && (
                                <ComponentsAvailabilityChecker
                                    locationId={RETAILER_ESTIMATES_LOCATION_ID}
                                    isSimulation={false}
                                    mode="physical"
                                    compact={false}
                                    lines={retailerEstimate.lines.map(
                                        (line) => ({
                                            variant_id:
                                                line.shopify_product_variant_id,
                                            quantity: line.quantity,
                                        })
                                    )}
                                />
                            )}

                            <Box
                                title={retailerEstimate?.number}
                                rightSection={EstimateBoxRightSection}
                            >
                                <div className="flex flex-col items-start gap-5">
                                    <div className="flex flex-col">
                                        <span className="">Informations</span>

                                        <div className="flex flex-col items-start text-gray-500 text-sm font-normal">
                                            <span>
                                                {dayjs(
                                                    retailerEstimate?.date
                                                ).format('ll')}
                                            </span>

                                            <span>
                                                {Intl.NumberFormat('fr-FR', {
                                                    style: 'currency',
                                                    currency: 'EUR',
                                                }).format(
                                                    retailerEstimate?.amounts
                                                        .total
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex flex-col">
                                        <span>Revendeur</span>
                                        <Anchor
                                            className="text-lg font-medium"
                                            href={`/retailers/${retailerEstimate.retailer.id}`}
                                        >
                                            {retailerEstimate.retailer.name}
                                        </Anchor>
                                    </div>

                                    <div className="flex flex-col">
                                        <span>Créé par</span>
                                        <span className="text-gray-500 text-sm">
                                            {retailerEstimate.created_by_profile
                                                ? retailerEstimate
                                                      .created_by_profile.name
                                                : 'Espace revendeur'}
                                        </span>
                                    </div>

                                    {retailerEstimate.invoices.length >
                                        0 && (
                                        <div className="flex flex-col w-full gap-2">
                                            <span>Factures associées</span>

                                            <div className="flex flex-col w-full gap-3">
                                                {retailerEstimate.invoices.map(
                                                    (invoice) => {
                                                        const invoiceStatus =
                                                            getInvoiceStatus(
                                                                invoice.status
                                                            )

                                                        return (
                                                            <Link
                                                                key={invoice.id}
                                                                className="grid grid-cols-[1fr_110px_110px_150px] w-full text-gray-500 py-2 items-center w-full hover:bg-gray-50 border rounded-lg px-3"
                                                                to={`/retailer_invoices/${invoice.id}`}
                                                            >
                                                                <div className="shrink-0">
                                                                    <Anchor component="span">
                                                                        {
                                                                            invoice.number
                                                                        }
                                                                    </Anchor>
                                                                </div>

                                                                <span className="text-gray-500 text-sm">
                                                                    {Intl.NumberFormat(
                                                                        'fr-FR',
                                                                        {
                                                                            style: 'currency',
                                                                            currency:
                                                                                'EUR',
                                                                        }
                                                                    ).format(
                                                                        invoice
                                                                            .amounts
                                                                            .total_incl_tax
                                                                    )}
                                                                </span>

                                                                <span className="text-gray-500 text-sm">
                                                                    {dayjs(
                                                                        invoice.date
                                                                    ).format(
                                                                        'll'
                                                                    )}
                                                                </span>
                                                                <div className="flex justify-end">
                                                                    <Badge
                                                                        size="xs"
                                                                        color={
                                                                            invoiceStatus.badgeColor
                                                                        }
                                                                    >
                                                                        {
                                                                            invoiceStatus.label
                                                                        }
                                                                    </Badge>
                                                                </div>
                                                            </Link>
                                                        )
                                                    }
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Box>

                            <RetailerEstimateFulfillmentOrders
                                estimate={retailerEstimate}
                                reloadEstimate={loadRetailerEstimate}
                            />

                            <Box
                                title="Fichier PDF"
                                rightSection={PDFBoxRightSection}
                            >
                                <embed
                                    src={retailerEstimate.pdf_link}
                                    className="w-full aspect-[0.7]"
                                    type="application/pdf"
                                />
                            </Box>
                        </div>
                    </div>

                    <Modal
                        opened={editStatusModalOpened}
                        title="Modifier le statut"
                        onClose={() => setEditStatusModalOpened(false)}
                    >
                        <Select
                            value={newStatus}
                            onChange={(value) => setNewStatus(value)}
                            data={statusOptions}
                        />

                        <div className="grid grid-cols-2 mt-5 gap-3">
                            <Button
                                variant="default"
                                onClick={() => setEditStatusModalOpened(false)}
                            >
                                Annuler
                            </Button>

                            <Button
                                loading={isSavingNewStatus}
                                onClick={saveNewStatus}
                            >
                                Modifier
                            </Button>
                        </div>
                    </Modal>
                </>
            )}
        </ScrollableScreen>
    )
}
